import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Copy, Printer, Download, QrCode, Check, X, Send } from "lucide-react";
import { adminGet, adminPost, adminPut, adminDel, authHeaders } from "@/lib/adminApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- Sélecteur de période ----------
const PERIODS = [
  ["today", "Aujourd'hui"], ["yesterday", "Hier"], ["7d", "7 derniers jours"], ["week", "Semaine en cours"],
  ["30d", "30 derniers jours"], ["month", "Mois en cours"], ["quarter", "Trimestre en cours"],
  ["6m", "6 mois"], ["12m", "12 mois"], ["year", "Année en cours"], ["all", "Depuis le début"], ["custom", "Période personnalisée"],
];

function periodRange(key, customFrom, customTo) {
  const iso = (d) => d.toISOString().slice(0, 10);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (key) {
    case "today": return [iso(today), iso(today)];
    case "yesterday": { const y = new Date(today); y.setDate(y.getDate() - 1); return [iso(y), iso(y)]; }
    case "7d": { const d = new Date(today); d.setDate(d.getDate() - 6); return [iso(d), iso(today)]; }
    case "week": { const d = new Date(today); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return [iso(d), iso(today)]; }
    case "30d": { const d = new Date(today); d.setDate(d.getDate() - 29); return [iso(d), iso(today)]; }
    case "month": return [iso(new Date(today.getFullYear(), today.getMonth(), 1)), iso(today)];
    case "quarter": { const q = Math.floor(today.getMonth() / 3) * 3; return [iso(new Date(today.getFullYear(), q, 1)), iso(today)]; }
    case "6m": { const d = new Date(today); d.setMonth(d.getMonth() - 6); return [iso(d), iso(today)]; }
    case "12m": { const d = new Date(today); d.setMonth(d.getMonth() - 12); return [iso(d), iso(today)]; }
    case "year": return [iso(new Date(today.getFullYear(), 0, 1)), iso(today)];
    case "custom": return [customFrom || null, customTo || null];
    default: return [null, null];
  }
}

function PeriodPicker({ period, setPeriod, customFrom, setCustomFrom, customTo, setCustomTo }) {
  return (
    <div className="rav-partners-period" data-testid="partners-period-picker">
      <select className="rav-input" data-testid="partners-period-select" value={period} onChange={(e) => setPeriod(e.target.value)}>
        {PERIODS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
      </select>
      {period === "custom" && (
        <>
          <input className="rav-input" data-testid="partners-period-from" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
          <input className="rav-input" data-testid="partners-period-to" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
        </>
      )}
    </div>
  );
}

// ---------- QR code (image servie par l'API, avec jeton admin) ----------
function QrImage({ linkId, url }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    let objectUrl = null;
    axios
      .get(`${API}/admin/partner-links/${linkId}/qr.png`, { params: { url }, headers: authHeaders(), responseType: "blob" })
      .then((r) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(r.data);
        setSrc(objectUrl);
      })
      .catch(() => {});
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [linkId, url]);
  if (!src) return null;
  return <img src={src} alt={`QR code ${url}`} data-testid={`qr-${linkId}`} style={{ width: 96, height: 96, borderRadius: 12 }} />;
}

const EMPTY_PARTNER = {
  name: "", last_name: "", company: "", trade_name: "", pseudo: "", email: "", phone: "",
  country: "", city: "", language: "fr", instagram: "", tiktok: "", facebook: "", youtube: "",
  linkedin: "", website: "", newsletter: "", other_channel: "", type_id: "", subtype_id: "",
  status_id: "", manager: "", notes: "", partnership_start: "", partnership_end: "", code: "",
};

const PARTNER_FIELDS = [
  ["name", "Prénom"], ["last_name", "Nom"], ["company", "Société / structure"], ["trade_name", "Nom commercial"],
  ["pseudo", "Pseudo"], ["email", "E-mail", "email"], ["phone", "Téléphone", "tel"], ["country", "Pays"], ["city", "Ville"],
  ["instagram", "Instagram"], ["tiktok", "TikTok"], ["facebook", "Facebook"], ["youtube", "YouTube"],
  ["linkedin", "LinkedIn"], ["website", "Site internet"], ["newsletter", "Newsletter"], ["other_channel", "Autre canal"],
  ["manager", "Responsable RAV"],
];

const fmtDate = (v) => (v ? String(v).slice(0, 10).split("-").reverse().join("/") : "—");

export default function Partners() {
  const [tab, setTab] = useState("dashboard");
  const [meta, setMeta] = useState({ types: [], subtypes: [], statuses: [], campaigns: [] });
  const [period, setPeriod] = useState("30d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [global, setGlobal] = useState(null);
  const [siteStats, setSiteStats] = useState(null);
  const [partners, setPartners] = useState([]);
  const [invites, setInvites] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [selected, setSelected] = useState(null); // fiche partenaire ouverte
  const [form, setForm] = useState(null); // formulaire création/édition
  const [inviteForm, setInviteForm] = useState(null);
  const [newInviteLink, setNewInviteLink] = useState("");
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [sortKey, setSortKey] = useState("signups");
  const [showArchived, setShowArchived] = useState(false);

  const [dateFrom, dateTo] = periodRange(period, customFrom, customTo);
  const periodQuery = dateFrom ? `?date_from=${dateFrom}&date_to=${dateTo || dateFrom}` : "";

  const loadMeta = useCallback(() => adminGet("/partner-meta").then(setMeta).catch(() => {}), []);
  const loadGlobal = useCallback(() => adminGet(`/partner-stats/global${periodQuery}`).then(setGlobal).catch(() => {}), [periodQuery]);
  const loadPartners = useCallback(() =>
    adminGet(`/partners?q=${encodeURIComponent(search)}&type_id=${filterType}&status_id=${filterStatus}&archived=${showArchived}`)
      .then((d) => setPartners(d.partners)).catch(() => {}), [search, filterType, filterStatus, showArchived]);
  const loadInvites = useCallback(() => adminGet("/partner-invites").then((d) => setInvites(d.invites)).catch(() => {}), []);
  const loadCfg = useCallback(() => adminGet("/partner-settings").then((d) => setCfg(d.settings)).catch(() => {}), []);
  const loadSiteStats = useCallback(() => adminGet("/site-stats?days=14").then(setSiteStats).catch(() => {}), []);

  useEffect(() => { loadMeta(); loadCfg(); }, [loadMeta, loadCfg]);
  useEffect(() => { loadGlobal(); loadSiteStats(); }, [loadGlobal, loadSiteStats]);
  useEffect(() => { loadPartners(); }, [loadPartners]);
  useEffect(() => { if (tab === "invitations") loadInvites(); }, [tab, loadInvites]);

  // F5 / retour sur la rubrique : rouvrir la fiche partenaire qui était affichée
  useEffect(() => {
    const pid = localStorage.getItem("rav-admin-partner-fiche");
    if (!pid) return;
    adminGet(`/partners/${pid}${periodQuery}`)
      .then(setSelected)
      .catch(() => localStorage.removeItem("rav-admin-partner-fiche"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusOf = (id) => meta.statuses.find((s) => s.id === id);
  const typeOf = (id) => meta.types.find((t) => t.id === id);
  const pendingCount = partners.filter((p) => statusOf(p.status_id)?.name === "En attente de validation").length;

  const partnerName = (p) => [p.name, p.last_name].filter(Boolean).join(" ") || p.company || p.pid;

  const openFiche = async (p) => {
    try {
      const d = await adminGet(`/partners/${p.id}${periodQuery}`);
      setSelected(d);
      localStorage.setItem("rav-admin-partner-fiche", p.id); // F5 : la fiche se rouvre
    } catch {
      toast("Impossible d'ouvrir la fiche partenaire.");
    }
  };
  const refreshFiche = () => selected && openFiche(selected.partner);

  const savePartner = async () => {
    try {
      if (form.id) await adminPut(`/partners/${form.id}`, form);
      else await adminPost("/partners", form);
      toast("Partenaire enregistré.");
      setForm(null);
      loadPartners();
      loadGlobal();
      if (form.id) refreshFiche();
    } catch {
      toast("Une erreur est survenue.");
    }
  };

  const validatePartner = async (p) => {
    await adminPost(`/partners/${p.id}/validate`, {}).catch(() => {});
    toast(`${partnerName(p)} est validé(e) — son espace et ses liens sont actifs.`);
    loadPartners();
    if (selected?.partner?.id === p.id) refreshFiche();
  };
  const rejectPartner = async (p) => {
    await adminPost(`/partners/${p.id}/reject`, {}).catch(() => {});
    toast(`${partnerName(p)} est refusé(e).`);
    loadPartners();
    if (selected?.partner?.id === p.id) refreshFiche();
  };
  const archivePartner = async (p) => {
    await adminDel(`/partners/${p.id}`).catch(() => {});
    toast("Partenaire archivé.");
    setSelected(null);
    localStorage.removeItem("rav-admin-partner-fiche");
    loadPartners();
  };

  const createInvite = async () => {
    try {
      const d = await adminPost("/partner-invites", { ...inviteForm, days: Number(inviteForm.days) || 30 });
      setNewInviteLink(`${window.location.origin}${d.path}`);
      setInviteForm(null);
      loadInvites();
      if (inviteForm.email) toast(`Invitation envoyée par e-mail à ${inviteForm.email} — lien prêt à copier aussi.`);
      else toast("Lien d'invitation créé — copiez-le et envoyez-le au partenaire.");
    } catch {
      toast("Une erreur est survenue.");
    }
  };

  const copyText = (text, label = "Lien copié.") => {
    navigator.clipboard?.writeText(text);
    toast(label);
  };

  const exportCsv = () => {
    axios
      .get(`${API}/admin/partner-stats/export.csv${periodQuery}`, { headers: authHeaders(), responseType: "blob" })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = "partenaires-rav.csv";
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast("Export impossible pour le moment."));
  };

  const sortedRows = useMemo(() => {
    if (!global) return [];
    return [...global.rows].sort((a, b) => (b[sortKey] ?? 0) - (a[sortKey] ?? 0));
  }, [global, sortKey]);

  const synthese = (d) => {
    if (!d) return "";
    const p = d.partner;
    const t = d.stats_total;
    const per = d.stats;
    const bestChannel = Object.entries(per.clicks_by_channel || {}).sort((a, b) => b[1] - a[1])[0];
    return `${partnerName(p)} a apporté ${t.signups} utilisatrice(s) depuis le début du partenariat. Sur la période affichée : ${per.clicks} clic(s), ${per.unique_visitors} visiteur(s) unique(s), ${per.signups} inscription(s), ${per.premium} abonnement(s) RAV+ et ${per.ca_total.toFixed(2)} € de chiffre d'affaires attribué.${bestChannel ? ` Son meilleur canal : ${bestChannel[0]}.` : ""} ${per.active_j30} utilisatrice(s) apportée(s) restent actives à J+30.`;
  };

  const TABS = [
    ["dashboard", "Tableau de bord"], ["partenaires", `Partenaires${pendingCount ? ` (${pendingCount} à valider)` : ""}`],
    ["invitations", "Invitations"], ["types", "Types & statuts"], ["campagnes", "Campagnes"], ["reglages", "Réglages"],
  ];

  return (
    <div data-testid="admin-partners-module">
      <h2 className="rav-admin-h2">Partenaires & Apporteurs</h2>
      <div className="rav-chips rav-partners-subnav rav-partners-noprint" data-testid="partners-tabs" style={{ marginBottom: 16 }}>
        {TABS.map(([k, label]) => (
          <button key={k} type="button" className={`rav-chip${tab === k ? " is-selected" : ""}`} data-testid={`partners-tab-${k}`} onClick={() => setTab(k)}>
            {label}
          </button>
        ))}
      </div>

      {tab !== "types" && tab !== "campagnes" && tab !== "reglages" && tab !== "invitations" && (
        <PeriodPicker period={period} setPeriod={setPeriod} customFrom={customFrom} setCustomFrom={setCustomFrom} customTo={customTo} setCustomTo={setCustomTo} />
      )}

      {/* ================= TABLEAU DE BORD ================= */}
      {tab === "dashboard" && global && (
        <div data-testid="partners-dashboard">
          {siteStats && (
            <>
              <h3 className="rav-admin-h3">Trafic du site (toutes sources)</h3>
              <div className="rav-admin-grid" data-testid="site-traffic">
                <div className="rav-admin-card" data-testid="traffic-visits"><div className="rav-admin-card-value">{siteStats.today.visits ?? 0}</div><div className="rav-admin-card-label">Visites aujourd'hui</div></div>
                <div className="rav-admin-card" data-testid="traffic-uniques"><div className="rav-admin-card-value">{siteStats.today.unique_visitors ?? 0}</div><div className="rav-admin-card-label">Visiteurs uniques aujourd'hui</div></div>
                <div className="rav-admin-card" data-testid="traffic-signups"><div className="rav-admin-card-value">{siteStats.today.signups ?? 0}</div><div className="rav-admin-card-label">Inscriptions aujourd'hui</div></div>
                <div className="rav-admin-card" data-testid="traffic-conversion"><div className="rav-admin-card-value">{siteStats.today.conversion ?? 0} %</div><div className="rav-admin-card-label">Visiteur → inscription</div></div>
              </div>
              <table className="rav-partners-table" data-testid="traffic-history">
                <thead><tr><th>Jour</th><th>Visites</th><th>Visiteurs uniques</th><th>Inscriptions</th><th>Conversion</th></tr></thead>
                <tbody>
                  {[...siteStats.days].reverse().map((d, i) => (
                    <tr key={d.date} data-testid={`traffic-row-${i}`}>
                      <td>{d.date.split("-").reverse().join("/")}</td><td>{d.visits}</td><td>{d.unique_visitors}</td><td>{d.signups}</td><td>{d.conversion} %</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="rav-page-sub">Comptage anonyme (aucune donnée personnelle) : permet de voir combien de personnes visitent le site sans s'inscrire.</p>
            </>
          )}
          <h3 className="rav-admin-h3">Réseau partenaires</h3>
          <div className="rav-admin-grid">
            <div className="rav-admin-card" data-testid="pkpi-total"><div className="rav-admin-card-value">{global.totals.partners_total}</div><div className="rav-admin-card-label">Partenaires</div></div>
            <div className="rav-admin-card" data-testid="pkpi-actifs"><div className="rav-admin-card-value">{global.totals.partners_active}</div><div className="rav-admin-card-label">Partenaires actifs</div></div>
            <div className="rav-admin-card" data-testid="pkpi-links"><div className="rav-admin-card-value">{global.totals.links_active}</div><div className="rav-admin-card-label">Liens actifs</div></div>
            <div className="rav-admin-card" data-testid="pkpi-clicks"><div className="rav-admin-card-value">{global.totals.clicks}</div><div className="rav-admin-card-label">Clics</div></div>
            <div className="rav-admin-card" data-testid="pkpi-visitors"><div className="rav-admin-card-value">{global.totals.unique_visitors}</div><div className="rav-admin-card-label">Visiteurs uniques</div></div>
            <div className="rav-admin-card" data-testid="pkpi-signups"><div className="rav-admin-card-value">{global.totals.signups}</div><div className="rav-admin-card-label">Utilisatrices apportées</div></div>
            <div className="rav-admin-card" data-testid="pkpi-j30"><div className="rav-admin-card-value">{global.totals.active_j30}</div><div className="rav-admin-card-label">Actives à J+30</div></div>
            <div className="rav-admin-card" data-testid="pkpi-premium"><div className="rav-admin-card-value">{global.totals.premium}</div><div className="rav-admin-card-label">Abonnements RAV+</div></div>
            <div className="rav-admin-card" data-testid="pkpi-ca"><div className="rav-admin-card-value">{global.totals.ca_total.toFixed(2)} €</div><div className="rav-admin-card-label">CA attribué</div></div>
            <div className="rav-admin-card" data-testid="pkpi-share"><div className="rav-admin-card-value">{global.totals.network_share} %</div><div className="rav-admin-card-label">Part du réseau partenaires</div></div>
          </div>

          <div className="rav-session-actions rav-partners-noprint" style={{ margin: "12px 0" }}>
            <button type="button" className="rav-secondary-button rav-session-btn" data-testid="partners-export-csv" onClick={exportCsv}>
              <Download size={14} strokeWidth={1.6} /> Exporter (CSV)
            </button>
            <button type="button" className="rav-secondary-button rav-session-btn" data-testid="partners-print" onClick={() => window.print()}>
              <Printer size={14} strokeWidth={1.6} /> Imprimer
            </button>
          </div>

          <h3 className="rav-admin-h3">Performance par partenaire</h3>
          <div style={{ overflowX: "auto" }}>
            <table className="rav-partners-table" data-testid="partners-table">
              <thead>
                <tr>
                  <th>Partenaire</th><th>Type</th><th>Statut</th>
                  {[["clicks", "Clics"], ["signups", "Inscrites"], ["active_j30", "J+30"], ["premium", "RAV+"], ["ca_total", "CA (€)"]].map(([k, label]) => (
                    <th key={k}><button type="button" className="rav-text-link" data-testid={`partners-sort-${k}`} onClick={() => setSortKey(k)}>{label}{sortKey === k ? " ↓" : ""}</button></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedRows.length === 0 && <tr><td colSpan={8}>Aucun partenaire pour le moment — ajoutez le premier dans l'onglet Partenaires.</td></tr>}
                {sortedRows.map((r, i) => (
                  <tr key={r.partner_id} data-testid={`partners-row-${i}`}>
                    <td><button type="button" className="rav-text-link" data-testid={`partners-open-${i}`} onClick={() => { setTab("partenaires"); openFiche({ id: r.partner_id }); }}>{r.name}<br /><small>{r.pid}</small></button></td>
                    <td>{r.type}</td><td>{r.status}</td>
                    <td>{r.clicks}</td><td>{r.signups}</td><td>{r.active_j30}</td><td>{r.premium}</td><td>{r.ca_total.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {global.by_type.length > 0 && (
            <>
              <h3 className="rav-admin-h3">Performance par type de partenaire</h3>
              <table className="rav-partners-table" data-testid="partners-types-table">
                <thead><tr><th>Type</th><th>Clics</th><th>Inscrites</th><th>RAV+</th><th>CA (€)</th></tr></thead>
                <tbody>
                  {global.by_type.map((t, i) => (
                    <tr key={i} data-testid={`partners-type-row-${i}`}><td>{t.type}</td><td>{t.clicks}</td><td>{t.signups}</td><td>{t.premium}</td><td>{t.ca_total.toFixed(2)}</td></tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* ================= PARTENAIRES ================= */}
      {tab === "partenaires" && !selected && (
        <div data-testid="partners-list-view">
          <div className="rav-session-actions rav-partners-noprint" style={{ flexWrap: "wrap", gap: 8 }}>
            <button type="button" className="rav-primary-button rav-admin-create" data-testid="partners-create" onClick={() => setForm({ ...EMPTY_PARTNER })}>
              <Plus size={14} strokeWidth={1.6} /> Ajouter un partenaire
            </button>
            <input className="rav-input" data-testid="partners-search" style={{ maxWidth: 240 }} placeholder="Rechercher…" value={search} onChange={(e) => setSearch(e.target.value)} />
            <select className="rav-input" data-testid="partners-filter-type" style={{ maxWidth: 200 }} value={filterType} onChange={(e) => setFilterType(e.target.value)}>
              <option value="">Tous les types</option>
              {meta.types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <select className="rav-input" data-testid="partners-filter-status" style={{ maxWidth: 200 }} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="">Tous les statuts</option>
              {meta.statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <label className="rav-field" style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
              <input type="checkbox" data-testid="partners-show-archived" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> <span>Archivés</span>
            </label>
          </div>

          <div className="rav-admin-list">
            {partners.length === 0 && <p className="rav-page-sub">Aucun partenaire. Créez le premier, ou envoyez une invitation depuis l'onglet Invitations.</p>}
            {partners.map((p, i) => {
              const st = statusOf(p.status_id);
              const tp = typeOf(p.type_id);
              const pending = st?.name === "En attente de validation";
              return (
                <div key={p.id} className="rav-admin-row" data-testid={`partner-row-${i}`}>
                  <span className="rav-admin-row-label">
                    {partnerName(p)} <small style={{ opacity: 0.7 }}>{p.pid}</small>
                    {pending && <span className="rav-partners-badge" data-testid={`partner-pending-${i}`} style={{ background: st?.color }}>À valider</span>}
                  </span>
                  <span className="rav-admin-row-sub">
                    {tp?.name || "Sans type"} · {st?.name || "—"} · {p.clicks_total} clic(s) · {p.signups_total} inscrite(s)
                  </span>
                  <span className="rav-admin-row-actions">
                    {pending && (
                      <>
                        <button type="button" className="rav-text-link" data-testid={`partner-validate-${i}`} onClick={() => validatePartner(p)}><Check size={13} strokeWidth={1.8} /> Valider</button>
                        <button type="button" className="rav-text-link" data-testid={`partner-reject-${i}`} onClick={() => rejectPartner(p)}><X size={13} strokeWidth={1.8} /> Refuser</button>
                      </>
                    )}
                    <button type="button" className="rav-text-link" data-testid={`partner-open-${i}`} onClick={() => openFiche(p)}>Ouvrir la fiche</button>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ================= FICHE PARTENAIRE ================= */}
      {tab === "partenaires" && selected && (
        <PartnerFiche
          data={selected}
          meta={meta}
          synthese={synthese(selected)}
          onBack={() => { setSelected(null); localStorage.removeItem("rav-admin-partner-fiche"); }}
          onEdit={() => setForm({ ...EMPTY_PARTNER, ...selected.partner })}
          onValidate={() => validatePartner(selected.partner)}
          onReject={() => rejectPartner(selected.partner)}
          onArchive={() => archivePartner(selected.partner)}
          onRefresh={refreshFiche}
          copyText={copyText}
          cfg={cfg}
        />
      )}

      {/* ================= FORMULAIRE PARTENAIRE ================= */}
      {form && (
        <div className="rav-admin-form" data-testid="partner-form">
          <h3 className="rav-admin-h3">{form.id ? `Modifier ${partnerName(form)}` : "Nouveau partenaire"}</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
            {PARTNER_FIELDS.map(([k, label, type]) => (
              <input key={k} className="rav-input" data-testid={`pf-${k}`} type={type || "text"} placeholder={label} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
            ))}
            <select className="rav-input" data-testid="pf-type" value={form.type_id || ""} onChange={(e) => setForm({ ...form, type_id: e.target.value || null, subtype_id: "" })}>
              <option value="">Type de partenaire…</option>
              {meta.types.filter((t) => t.active).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <select className="rav-input" data-testid="pf-subtype" value={form.subtype_id || ""} onChange={(e) => setForm({ ...form, subtype_id: e.target.value || null })}>
              <option value="">Sous-type (facultatif)…</option>
              {meta.subtypes.filter((s) => s.type_id === form.type_id && s.active).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select className="rav-input" data-testid="pf-status" value={form.status_id || ""} onChange={(e) => setForm({ ...form, status_id: e.target.value || null })}>
              <option value="">Statut…</option>
              {meta.statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <input className="rav-input" data-testid="pf-code" placeholder="Code partenaire (ex. MARIE-RAV)" value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
            <label className="rav-field"><span>Début du partenariat</span><input className="rav-input" data-testid="pf-start" type="date" value={form.partnership_start || ""} onChange={(e) => setForm({ ...form, partnership_start: e.target.value || null })} /></label>
            <label className="rav-field"><span>Fin éventuelle</span><input className="rav-input" data-testid="pf-end" type="date" value={form.partnership_end || ""} onChange={(e) => setForm({ ...form, partnership_end: e.target.value || null })} /></label>
          </div>
          <textarea className="rav-input" data-testid="pf-notes" rows={3} placeholder="Notes internes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} style={{ marginTop: 10 }} />
          <div className="rav-session-actions">
            <button type="button" className="rav-secondary-button rav-session-btn" data-testid="pf-save" onClick={savePartner}>Enregistrer</button>
            <button type="button" className="rav-text-link" data-testid="pf-cancel" onClick={() => setForm(null)}>Annuler</button>
          </div>
        </div>
      )}

      {/* ================= INVITATIONS ================= */}
      {tab === "invitations" && (
        <div data-testid="partners-invitations">
          <p className="rav-page-sub">
            Envoyez un lien d'invitation : le partenaire crée son espace, qui reste <strong>non actif</strong> jusqu'à votre validation (onglet Partenaires).
          </p>
          <button type="button" className="rav-primary-button rav-admin-create" data-testid="invite-create" onClick={() => setInviteForm({ label: "", email: "", days: 30 })}>
            <Send size={14} strokeWidth={1.6} /> Inviter un partenaire
          </button>
          {inviteForm && (
            <div className="rav-admin-form" data-testid="invite-form">
              <input className="rav-input" data-testid="invite-label" placeholder="Nom du partenaire (facultatif)" value={inviteForm.label} onChange={(e) => setInviteForm({ ...inviteForm, label: e.target.value })} />
              <input className="rav-input" data-testid="invite-email" type="email" placeholder="E-mail du partenaire (facultatif — sinon copiez le lien)" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value.toLowerCase() })} />
              <label className="rav-field"><span>Validité du lien (jours)</span>
                <input className="rav-input" data-testid="invite-days" type="number" min="1" value={inviteForm.days} onChange={(e) => setInviteForm({ ...inviteForm, days: e.target.value })} />
              </label>
              <div className="rav-session-actions">
                <button type="button" className="rav-secondary-button rav-session-btn" data-testid="invite-save" onClick={createInvite}>Créer l'invitation</button>
                <button type="button" className="rav-text-link" data-testid="invite-cancel" onClick={() => setInviteForm(null)}>Annuler</button>
              </div>
            </div>
          )}
          {newInviteLink && (
            <div className="rav-limit-card" data-testid="invite-link-card">
              <p style={{ margin: 0, wordBreak: "break-all" }}><strong>Lien à envoyer :</strong> {newInviteLink}</p>
              <button type="button" className="rav-secondary-button rav-session-btn" data-testid="invite-copy" style={{ marginTop: 8 }} onClick={() => copyText(newInviteLink)}>
                <Copy size={14} strokeWidth={1.6} /> Copier le lien
              </button>
            </div>
          )}
          <div className="rav-admin-list">
            {invites.map((inv, i) => (
              <div key={inv.id} className="rav-admin-row" data-testid={`invite-row-${i}`}>
                <span className="rav-admin-row-label">{inv.label || inv.email || "Invitation"}</span>
                <span className="rav-admin-row-sub">
                  {inv.used_at ? `Utilisée le ${fmtDate(inv.used_at)}` : `Valable jusqu'au ${fmtDate(inv.expires_at)}`}
                </span>
                <span className="rav-admin-row-actions">
                  {!inv.used_at && (
                    <button type="button" className="rav-text-link" data-testid={`invite-copy-${i}`} onClick={() => copyText(`${window.location.origin}/partenaire/inscription?invite=${inv.token}`)}>Copier le lien</button>
                  )}
                  <button type="button" className="rav-text-link" data-testid={`invite-delete-${i}`} onClick={() => adminDel(`/partner-invites/${inv.id}`).then(loadInvites)}>Révoquer</button>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ================= TYPES & STATUTS ================= */}
      {tab === "types" && <TypesManager meta={meta} onChanged={loadMeta} />}

      {/* ================= CAMPAGNES ================= */}
      {tab === "campagnes" && <CampaignsManager campaigns={meta.campaigns} onChanged={loadMeta} />}

      {/* ================= RÉGLAGES ================= */}
      {tab === "reglages" && cfg && (
        <div className="rav-admin-form" data-testid="partners-settings">
          <label className="rav-field"><span>Fenêtre d'attribution (jours entre le clic et l'inscription)</span>
            <select className="rav-input" data-testid="ps-window" value={cfg.attribution_window_days} onChange={(e) => setCfg({ ...cfg, attribution_window_days: Number(e.target.value) })}>
              {[7, 15, 30, 60, 90].map((d) => <option key={d} value={d}>{d} jours</option>)}
            </select>
          </label>
          <label className="rav-field"><span>Si plusieurs partenaires ont été visités, retenir :</span>
            <select className="rav-input" data-testid="ps-rule" value={cfg.attribution_rule} onChange={(e) => setCfg({ ...cfg, attribution_rule: e.target.value })}>
              <option value="last">Le dernier partenaire avant l'inscription (recommandé)</option>
              <option value="first">Le premier partenaire rencontré</option>
            </select>
          </label>
          <label className="rav-field" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            <input type="checkbox" data-testid="ps-commissions" checked={!!cfg.commissions_enabled} onChange={(e) => setCfg({ ...cfg, commissions_enabled: e.target.checked })} />
            <span>Activer le suivi des commissions (à 0 € par défaut, aucune rémunération automatique)</span>
          </label>
          <div className="rav-session-actions">
            <button type="button" className="rav-secondary-button rav-session-btn" data-testid="ps-save" onClick={() => adminPut("/partner-settings", cfg).then(() => toast("Réglages enregistrés.")).catch(() => toast("Une erreur est survenue."))}>
              Enregistrer les réglages
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Fiche partenaire ----------
function PartnerFiche({ data, meta, synthese, onBack, onEdit, onValidate, onReject, onArchive, onRefresh, copyText, cfg }) {
  const { partner: p, links, benefits, commissions, stats, stats_total: total, type, status, benefit_kinds } = data;
  const [linkForm, setLinkForm] = useState(null);
  const [benefitForm, setBenefitForm] = useState(null);
  const [commissionForm, setCommissionForm] = useState(null);
  const linkUrl = (slug) => `${window.location.origin}/p/${slug}`;
  const pending = status?.name === "En attente de validation";

  const saveLink = async () => {
    try {
      if (linkForm.id) await adminPut(`/partner-links/${linkForm.id}`, linkForm);
      else await adminPost(`/partners/${p.id}/links`, linkForm);
      toast("Lien enregistré.");
      setLinkForm(null);
      onRefresh();
    } catch (err) {
      toast(err.response?.data?.detail || "Une erreur est survenue.");
    }
  };

  const saveBenefit = async () => {
    try {
      const d = await adminPost(`/partners/${p.id}/benefits`, benefitForm);
      toast(d.benefit.status === "en attente"
        ? "Avantage enregistré — il s'appliquera automatiquement quand le partenaire créera son compte RAV."
        : "Avantage activé sur le compte du partenaire (0 €, sans carte ni prélèvement).");
      setBenefitForm(null);
      onRefresh();
    } catch {
      toast("Une erreur est survenue.");
    }
  };

  const saveCommission = async () => {
    try {
      const payload = { ...commissionForm, ca_attributed: Number(commissionForm.ca_attributed) || 0, base: Number(commissionForm.base) || 0, calculated: Number(commissionForm.calculated) || 0 };
      if (commissionForm.id) await adminPut(`/partner-commissions/${commissionForm.id}`, payload);
      else await adminPost(`/partners/${p.id}/commissions`, payload);
      toast("Commission enregistrée.");
      setCommissionForm(null);
      onRefresh();
    } catch {
      toast("Une erreur est survenue.");
    }
  };

  const kpis = [
    ["Clics", stats.clicks], ["Visiteurs uniques", stats.unique_visitors], ["Utilisatrices apportées", stats.signups],
    ["Premier Instant", stats.first_instant], ["Actives J+7", stats.active_j7], ["Actives J+30", stats.active_j30],
    ["RAV+", stats.premium], ["Programmes", stats.programmes], ["Visios", stats.visios], ["CA attribué", `${stats.ca_total.toFixed(2)} €`],
  ];

  return (
    <div data-testid="partner-fiche">
      <button type="button" className="rav-text-link rav-partners-noprint" data-testid="fiche-back" onClick={onBack}>← Tous les partenaires</button>
      <h3 className="rav-admin-h3" data-testid="fiche-title">
        {[p.name, p.last_name].filter(Boolean).join(" ") || p.company} <small style={{ opacity: 0.7 }}>{p.pid}</small>
      </h3>
      <p className="rav-page-sub" data-testid="fiche-subtitle">
        {type?.name || "Sans type"}{status ? ` · ${status.name}` : ""} · Code partenaire : <strong>{p.code}</strong>
        {p.email ? ` · ${p.email}` : ""}
      </p>

      <div className="rav-limit-card" data-testid="fiche-synthese">
        <p style={{ margin: 0 }}>{synthese}</p>
      </div>

      <div className="rav-admin-grid">
        {kpis.map(([label, v], i) => (
          <div key={label} className="rav-admin-card" data-testid={`fiche-kpi-${i}`}>
            <div className="rav-admin-card-value">{v}</div>
            <div className="rav-admin-card-label">{label}</div>
          </div>
        ))}
      </div>
      <p className="rav-page-sub">Depuis le début du partenariat : {total.signups} inscrite(s), {total.premium} RAV+, {total.ca_total.toFixed(2)} € de CA attribué.</p>

      {/* ----- Analyses & chiffre d'affaires du partenaire ----- */}
      <h3 className="rav-admin-h3">Analyses & chiffre d'affaires</h3>
      <div className="rav-admin-grid" data-testid="fiche-ca-analysis">
        <div className="rav-admin-card" data-testid="fca-total">
          <div className="rav-admin-card-value">{stats.ca_total.toFixed(2)} €</div>
          <div className="rav-admin-card-label">CA attribué (période affichée)</div>
        </div>
        <div className="rav-admin-card" data-testid="fca-total-all">
          <div className="rav-admin-card-value">{total.ca_total.toFixed(2)} €</div>
          <div className="rav-admin-card-label">CA cumulé depuis le début</div>
        </div>
        <div className="rav-admin-card" data-testid="fca-avg-user">
          <div className="rav-admin-card-value">{stats.signups ? `${(stats.ca_total / stats.signups).toFixed(2)} €` : "—"}</div>
          <div className="rav-admin-card-label">CA moyen par utilisatrice apportée</div>
        </div>
        <div className="rav-admin-card" data-testid="fca-avg-premium">
          <div className="rav-admin-card-value">{stats.premium ? `${(stats.ca_total / stats.premium).toFixed(2)} €` : "—"}</div>
          <div className="rav-admin-card-label">CA moyen par abonnée RAV+</div>
        </div>
      </div>
      {Object.keys(stats.ca_by_kind || {}).length > 0 ? (
        <table className="rav-partners-table" data-testid="fca-by-kind">
          <thead><tr><th>Nature du revenu</th><th>Montant attribué</th></tr></thead>
          <tbody>
            {Object.entries(stats.ca_by_kind).map(([kind, amount], i) => (
              <tr key={kind} data-testid={`fca-kind-${i}`}>
                <td>{{ abonnement: "Abonnements RAV+", programme: "Programmes", visio: "Visios", cadeau: "Cartes cadeaux", gift: "Cartes cadeaux", subscription: "Abonnements RAV+" }[kind] || kind}</td>
                <td>{Number(amount).toFixed(2)} €</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="rav-page-sub" data-testid="fca-empty">Aucun revenu attribué à ce partenaire sur la période — les abonnements, programmes, visios et cartes cadeaux des personnes apportées apparaîtront ici automatiquement.</p>
      )}

      <h3 className="rav-admin-h3">Tunnel d'acquisition (période affichée)</h3>
      <div data-testid="fiche-funnel">
        {[
          ["Clics", stats.clicks],
          ["Visiteurs uniques", stats.unique_visitors],
          ["Inscriptions", stats.signups],
          ["Premier Instant", stats.first_instant],
          ["Actives à J+30", stats.active_j30],
          ["Abonnements RAV+", stats.premium],
        ].map(([label, v], i, arr) => {
          const max = Math.max(1, arr[0][1]);
          return (
            <div key={label} className="rav-bar-row" data-testid={`funnel-${i}`}>
              <span className="rav-bar-label">{label}</span>
              <span className="rav-bar-track"><span className="rav-bar-fill" style={{ width: `${(v / max) * 100}%` }} /></span>
              <span className="rav-bar-value">{v}</span>
            </div>
          );
        })}
        <p className="rav-page-sub">
          Conversion clic → inscription : <strong data-testid="funnel-conv-click">{stats.clicks ? `${Math.round((100 * stats.signups) / stats.clicks)} %` : "—"}</strong>
          {" · "}inscription → RAV+ : <strong data-testid="funnel-conv-premium">{stats.signups ? `${Math.round((100 * stats.premium) / stats.signups)} %` : "—"}</strong>
        </p>
      </div>

      <div className="rav-session-actions rav-partners-noprint" style={{ flexWrap: "wrap", gap: 8 }}>
        {pending && (
          <>
            <button type="button" className="rav-primary-button rav-session-btn" data-testid="fiche-validate" onClick={onValidate}><Check size={14} strokeWidth={1.8} /> Valider ce partenaire</button>
            <button type="button" className="rav-secondary-button rav-session-btn" data-testid="fiche-reject" onClick={onReject}><X size={14} strokeWidth={1.8} /> Refuser</button>
          </>
        )}
        <button type="button" className="rav-secondary-button rav-session-btn" data-testid="fiche-edit" onClick={onEdit}>Modifier la fiche</button>
        <button type="button" className="rav-secondary-button rav-session-btn" data-testid="fiche-print" onClick={() => window.print()}><Printer size={14} strokeWidth={1.6} /> Imprimer le rapport</button>
        <button type="button" className="rav-text-link" data-testid="fiche-archive" onClick={onArchive}>Archiver</button>
      </div>

      {/* ----- Liens ----- */}
      <h3 className="rav-admin-h3">Liens partenaires</h3>
      <button type="button" className="rav-secondary-button rav-session-btn rav-partners-noprint" data-testid="link-create" onClick={() => setLinkForm({ name: "", channel: "", campaign_id: "", destination: "/", slug: "", start_date: "", end_date: "", active: true })}>
        <Plus size={14} strokeWidth={1.6} /> Créer un lien
      </button>
      {linkForm && (
        <div className="rav-admin-form" data-testid="link-form">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
            <input className="rav-input" data-testid="lf-name" placeholder="Nom du lien (ex. Instagram)" value={linkForm.name} onChange={(e) => setLinkForm({ ...linkForm, name: e.target.value })} />
            <input className="rav-input" data-testid="lf-channel" placeholder="Canal (Instagram, TikTok, QR Code…)" value={linkForm.channel} onChange={(e) => setLinkForm({ ...linkForm, channel: e.target.value })} />
            <select className="rav-input" data-testid="lf-campaign" value={linkForm.campaign_id || ""} onChange={(e) => setLinkForm({ ...linkForm, campaign_id: e.target.value || null })}>
              <option value="">Campagne (facultatif)…</option>
              {meta.campaigns.filter((c) => c.active).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <input className="rav-input" data-testid="lf-destination" placeholder="Destination (/accueil, /offres…)" value={linkForm.destination} onChange={(e) => setLinkForm({ ...linkForm, destination: e.target.value })} />
            {!linkForm.id && <input className="rav-input" data-testid="lf-slug" placeholder="Adresse personnalisée (facultatif)" value={linkForm.slug} onChange={(e) => setLinkForm({ ...linkForm, slug: e.target.value })} />}
            <label className="rav-field"><span>Début</span><input className="rav-input" data-testid="lf-start" type="date" value={linkForm.start_date || ""} onChange={(e) => setLinkForm({ ...linkForm, start_date: e.target.value || null })} /></label>
            <label className="rav-field"><span>Fin</span><input className="rav-input" data-testid="lf-end" type="date" value={linkForm.end_date || ""} onChange={(e) => setLinkForm({ ...linkForm, end_date: e.target.value || null })} /></label>
            <label className="rav-field" style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
              <input type="checkbox" data-testid="lf-active" checked={!!linkForm.active} onChange={(e) => setLinkForm({ ...linkForm, active: e.target.checked })} /> <span>Lien actif</span>
            </label>
          </div>
          <div className="rav-session-actions">
            <button type="button" className="rav-secondary-button rav-session-btn" data-testid="lf-save" onClick={saveLink}>Enregistrer le lien</button>
            <button type="button" className="rav-text-link" data-testid="lf-cancel" onClick={() => setLinkForm(null)}>Annuler</button>
          </div>
        </div>
      )}
      <div className="rav-admin-list">
        {links.map((l, i) => (
          <div key={l.id} className="rav-admin-row" data-testid={`link-row-${i}`} style={{ alignItems: "center" }}>
            <QrImage linkId={l.id} url={linkUrl(l.slug)} />
            <span className="rav-admin-row-label">{l.name}{!l.active ? " (inactif)" : ""}</span>
            <span className="rav-admin-row-sub">{linkUrl(l.slug)}{l.channel ? ` · ${l.channel}` : ""}</span>
            <span className="rav-admin-row-actions">
              <button type="button" className="rav-text-link" data-testid={`link-copy-${i}`} onClick={() => copyText(linkUrl(l.slug))}><Copy size={13} strokeWidth={1.6} /> Copier</button>
              <button type="button" className="rav-text-link" data-testid={`link-share-${i}`} onClick={() => copyText(`Respire Aime Vibre — L'Instant qui vous ressemble 🤎\nOffrez-vous un instant de sophrologie personnalisé : ${linkUrl(l.slug)}`, "Message de partage copié (avec le slogan RAV).")}>Message de partage</button>
              <button type="button" className="rav-text-link" data-testid={`link-edit-${i}`} onClick={() => setLinkForm({ ...l, campaign_id: l.campaign_id || "", start_date: l.start_date || "", end_date: l.end_date || "" })}>Modifier</button>
              <button type="button" className="rav-text-link" data-testid={`link-delete-${i}`} onClick={() => adminDel(`/partner-links/${l.id}`).then(onRefresh)}><Trash2 size={13} strokeWidth={1.6} /></button>
            </span>
          </div>
        ))}
      </div>

      {/* ----- Avantages ----- */}
      <h3 className="rav-admin-h3">Avantages offerts par Julie</h3>
      <p className="rav-page-sub">Sans carte bancaire, sans prélèvement, sans renouvellement payant : à expiration, le compte repasse simplement en accès découverte.</p>
      <button type="button" className="rav-secondary-button rav-session-btn rav-partners-noprint" data-testid="benefit-create" onClick={() => setBenefitForm({ kind: "ravplus_1m", email: p.email || "", note: "" })}>
        <Plus size={14} strokeWidth={1.6} /> Offrir un avantage
      </button>
      {benefitForm && (
        <div className="rav-admin-form" data-testid="benefit-form">
          <select className="rav-input" data-testid="bf-kind" value={benefitForm.kind} onChange={(e) => setBenefitForm({ ...benefitForm, kind: e.target.value })}>
            {benefit_kinds.map((b) => <option key={b.kind} value={b.kind}>{b.label}</option>)}
          </select>
          <input className="rav-input" data-testid="bf-email" type="email" placeholder="E-mail du compte RAV du partenaire" value={benefitForm.email} onChange={(e) => setBenefitForm({ ...benefitForm, email: e.target.value.toLowerCase() })} />
          <input className="rav-input" data-testid="bf-note" placeholder="Note interne (facultatif)" value={benefitForm.note} onChange={(e) => setBenefitForm({ ...benefitForm, note: e.target.value })} />
          <div className="rav-session-actions">
            <button type="button" className="rav-secondary-button rav-session-btn" data-testid="bf-save" onClick={saveBenefit}>Activer l'avantage</button>
            <button type="button" className="rav-text-link" data-testid="bf-cancel" onClick={() => setBenefitForm(null)}>Annuler</button>
          </div>
        </div>
      )}
      <div className="rav-admin-list">
        {benefits.map((b, i) => (
          <div key={b.id} className="rav-admin-row" data-testid={`benefit-row-${i}`}>
            <span className="rav-admin-row-label">{b.label}</span>
            <span className="rav-admin-row-sub">
              {fmtDate(b.created_at)} · {b.status}{b.gift_until ? ` · jusqu'au ${fmtDate(b.gift_until)}` : ""}{b.note ? ` · ${b.note}` : ""}
            </span>
            <span className="rav-admin-row-actions">
              <button type="button" className="rav-text-link" data-testid={`benefit-delete-${i}`} onClick={() => adminDel(`/partner-benefits/${b.id}`).then(onRefresh)}><Trash2 size={13} strokeWidth={1.6} /></button>
            </span>
          </div>
        ))}
        {benefits.length === 0 && <p className="rav-page-sub">Aucun avantage pour le moment.</p>}
      </div>

      {/* ----- Commissions (désactivées / 0 € par défaut) ----- */}
      {cfg?.commissions_enabled && (
        <>
          <h3 className="rav-admin-h3">Commissions</h3>
          <button type="button" className="rav-secondary-button rav-session-btn rav-partners-noprint" data-testid="commission-create" onClick={() => setCommissionForm({ label: "", ca_attributed: total.ca_total, base: 0, calculated: 0, status: "à valider", reference: "" })}>
            <Plus size={14} strokeWidth={1.6} /> Ajouter une commission
          </button>
          {commissionForm && (
            <div className="rav-admin-form" data-testid="commission-form">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10 }}>
                <input className="rav-input" data-testid="cf-label" placeholder="Libellé" value={commissionForm.label} onChange={(e) => setCommissionForm({ ...commissionForm, label: e.target.value })} />
                <input className="rav-input" data-testid="cf-ca" type="number" step="0.01" placeholder="CA attribué (€)" value={commissionForm.ca_attributed} onChange={(e) => setCommissionForm({ ...commissionForm, ca_attributed: e.target.value })} />
                <input className="rav-input" data-testid="cf-base" type="number" step="0.01" placeholder="Base commissionnable (€)" value={commissionForm.base} onChange={(e) => setCommissionForm({ ...commissionForm, base: e.target.value })} />
                <input className="rav-input" data-testid="cf-calc" type="number" step="0.01" placeholder="Commission (€)" value={commissionForm.calculated} onChange={(e) => setCommissionForm({ ...commissionForm, calculated: e.target.value })} />
                <select className="rav-input" data-testid="cf-status" value={commissionForm.status} onChange={(e) => setCommissionForm({ ...commissionForm, status: e.target.value })}>
                  <option value="à valider">À valider</option><option value="validée">Validée</option><option value="payée">Payée</option>
                </select>
                <input className="rav-input" data-testid="cf-ref" placeholder="Référence du règlement" value={commissionForm.reference} onChange={(e) => setCommissionForm({ ...commissionForm, reference: e.target.value })} />
              </div>
              <div className="rav-session-actions">
                <button type="button" className="rav-secondary-button rav-session-btn" data-testid="cf-save" onClick={saveCommission}>Enregistrer</button>
                <button type="button" className="rav-text-link" data-testid="cf-cancel" onClick={() => setCommissionForm(null)}>Annuler</button>
              </div>
            </div>
          )}
          <div className="rav-admin-list">
            {commissions.map((c, i) => (
              <div key={c.id} className="rav-admin-row" data-testid={`commission-row-${i}`}>
                <span className="rav-admin-row-label">{c.label || "Commission"}</span>
                <span className="rav-admin-row-sub">{c.calculated} € · {c.status}{c.reference ? ` · ${c.reference}` : ""}</span>
                <span className="rav-admin-row-actions">
                  <button type="button" className="rav-text-link" data-testid={`commission-edit-${i}`} onClick={() => setCommissionForm({ ...c })}>Modifier</button>
                  <button type="button" className="rav-text-link" data-testid={`commission-delete-${i}`} onClick={() => adminDel(`/partner-commissions/${c.id}`).then(onRefresh)}><Trash2 size={13} strokeWidth={1.6} /></button>
                </span>
              </div>
            ))}
          </div>
        </>
      )}
      <p className="rav-page-sub rav-partners-noprint" style={{ marginTop: 12 }}>
        <QrCode size={13} strokeWidth={1.6} /> Astuce : chaque QR code peut être téléchargé (clic droit → enregistrer) pour vos flyers, affiches et cartes de visite.
      </p>
    </div>
  );
}

// ---------- Types, sous-types et statuts administrables ----------
function TypesManager({ meta, onChanged }) {
  const [typeForm, setTypeForm] = useState(null);
  const [subForm, setSubForm] = useState(null);
  const [statusForm, setStatusForm] = useState(null);

  const saveType = async () => {
    const payload = { ...typeForm, order: Number(typeForm.order) || 0 };
    if (typeForm.id) await adminPut(`/partner-types/${typeForm.id}`, payload).catch(() => {});
    else await adminPost("/partner-types", payload).catch(() => {});
    toast("Type enregistré.");
    setTypeForm(null);
    onChanged();
  };
  const saveSub = async () => {
    const payload = { ...subForm, order: Number(subForm.order) || 0 };
    if (subForm.id) await adminPut(`/partner-subtypes/${subForm.id}`, payload).catch(() => {});
    else await adminPost("/partner-subtypes", payload).catch(() => {});
    toast("Sous-type enregistré.");
    setSubForm(null);
    onChanged();
  };
  const saveStatus = async () => {
    const payload = { ...statusForm, order: Number(statusForm.order) || 0 };
    if (statusForm.id) await adminPut(`/partner-statuses/${statusForm.id}`, payload).catch(() => {});
    else await adminPost("/partner-statuses", payload).catch(() => {});
    toast("Statut enregistré.");
    setStatusForm(null);
    onChanged();
  };

  return (
    <div data-testid="partners-types-manager">
      <h3 className="rav-admin-h3">Types de partenaires</h3>
      <button type="button" className="rav-secondary-button rav-session-btn" data-testid="type-create" onClick={() => setTypeForm({ name: "", color: "#B98A2F", icon: "", description: "", order: meta.types.length + 1, active: true })}>
        <Plus size={14} strokeWidth={1.6} /> Ajouter un type
      </button>
      {typeForm && (
        <div className="rav-admin-form" data-testid="type-form">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 10 }}>
            <input className="rav-input" data-testid="tf-name" placeholder="Nom du type" value={typeForm.name} onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })} />
            <label className="rav-field"><span>Couleur</span><input className="rav-input" data-testid="tf-color" type="color" value={typeForm.color} onChange={(e) => setTypeForm({ ...typeForm, color: e.target.value })} /></label>
            <input className="rav-input" data-testid="tf-icon" placeholder="Icône (facultatif)" value={typeForm.icon} onChange={(e) => setTypeForm({ ...typeForm, icon: e.target.value })} />
            <input className="rav-input" data-testid="tf-order" type="number" placeholder="Ordre" value={typeForm.order} onChange={(e) => setTypeForm({ ...typeForm, order: e.target.value })} />
          </div>
          <input className="rav-input" data-testid="tf-description" placeholder="Description interne" value={typeForm.description} onChange={(e) => setTypeForm({ ...typeForm, description: e.target.value })} style={{ marginTop: 8 }} />
          <label className="rav-field" style={{ flexDirection: "row", alignItems: "center", gap: 6, marginTop: 8 }}>
            <input type="checkbox" data-testid="tf-active" checked={!!typeForm.active} onChange={(e) => setTypeForm({ ...typeForm, active: e.target.checked })} /> <span>Type actif</span>
          </label>
          <div className="rav-session-actions">
            <button type="button" className="rav-secondary-button rav-session-btn" data-testid="tf-save" onClick={saveType}>Enregistrer</button>
            <button type="button" className="rav-text-link" data-testid="tf-cancel" onClick={() => setTypeForm(null)}>Annuler</button>
          </div>
        </div>
      )}
      <div className="rav-admin-list">
        {meta.types.map((t, i) => (
          <div key={t.id} className="rav-admin-row" data-testid={`type-row-${i}`}>
            <span className="rav-partners-badge" style={{ background: t.color }}> </span>
            <span className="rav-admin-row-label">{t.name}{!t.active ? " (désactivé)" : ""}</span>
            <span className="rav-admin-row-sub">{t.description || ""}</span>
            <span className="rav-admin-row-actions">
              <button type="button" className="rav-text-link" data-testid={`type-add-sub-${i}`} onClick={() => setSubForm({ type_id: t.id, name: "", order: 0, active: true })}>+ Sous-type</button>
              <button type="button" className="rav-text-link" data-testid={`type-edit-${i}`} onClick={() => setTypeForm({ ...t })}>Modifier</button>
              <button type="button" className="rav-text-link" data-testid={`type-archive-${i}`} onClick={() => adminDel(`/partner-types/${t.id}`).then(onChanged)}>Archiver</button>
            </span>
          </div>
        ))}
      </div>

      <h3 className="rav-admin-h3">Sous-types</h3>
      {subForm && (
        <div className="rav-admin-form" data-testid="subtype-form">
          <input className="rav-input" data-testid="sf-name" placeholder="Nom du sous-type (ex. Sophrologue)" value={subForm.name} onChange={(e) => setSubForm({ ...subForm, name: e.target.value })} />
          <div className="rav-session-actions">
            <button type="button" className="rav-secondary-button rav-session-btn" data-testid="sf-save" onClick={saveSub}>Enregistrer</button>
            <button type="button" className="rav-text-link" data-testid="sf-cancel" onClick={() => setSubForm(null)}>Annuler</button>
          </div>
        </div>
      )}
      <div className="rav-admin-list">
        {meta.subtypes.map((s, i) => (
          <div key={s.id} className="rav-admin-row" data-testid={`subtype-row-${i}`}>
            <span className="rav-admin-row-label">{s.name}</span>
            <span className="rav-admin-row-sub">{meta.types.find((t) => t.id === s.type_id)?.name || ""}</span>
            <span className="rav-admin-row-actions">
              <button type="button" className="rav-text-link" data-testid={`subtype-edit-${i}`} onClick={() => setSubForm({ ...s })}>Modifier</button>
              <button type="button" className="rav-text-link" data-testid={`subtype-archive-${i}`} onClick={() => adminDel(`/partner-subtypes/${s.id}`).then(onChanged)}>Archiver</button>
            </span>
          </div>
        ))}
        {meta.subtypes.length === 0 && <p className="rav-page-sub">Aucun sous-type — ajoutez-en depuis un type ci-dessus.</p>}
      </div>

      <h3 className="rav-admin-h3">Statuts des partenaires</h3>
      <button type="button" className="rav-secondary-button rav-session-btn" data-testid="status-create" onClick={() => setStatusForm({ name: "", color: "#8A8A8A", order: meta.statuses.length + 1, active: true })}>
        <Plus size={14} strokeWidth={1.6} /> Ajouter un statut
      </button>
      {statusForm && (
        <div className="rav-admin-form" data-testid="status-form">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 10 }}>
            <input className="rav-input" data-testid="stf-name" placeholder="Nom du statut" value={statusForm.name} onChange={(e) => setStatusForm({ ...statusForm, name: e.target.value })} />
            <label className="rav-field"><span>Couleur</span><input className="rav-input" data-testid="stf-color" type="color" value={statusForm.color} onChange={(e) => setStatusForm({ ...statusForm, color: e.target.value })} /></label>
            <input className="rav-input" data-testid="stf-order" type="number" placeholder="Ordre" value={statusForm.order} onChange={(e) => setStatusForm({ ...statusForm, order: e.target.value })} />
          </div>
          <div className="rav-session-actions">
            <button type="button" className="rav-secondary-button rav-session-btn" data-testid="stf-save" onClick={saveStatus}>Enregistrer</button>
            <button type="button" className="rav-text-link" data-testid="stf-cancel" onClick={() => setStatusForm(null)}>Annuler</button>
          </div>
        </div>
      )}
      <div className="rav-admin-list">
        {meta.statuses.map((s, i) => (
          <div key={s.id} className="rav-admin-row" data-testid={`status-row-${i}`}>
            <span className="rav-partners-badge" style={{ background: s.color }}> </span>
            <span className="rav-admin-row-label">{s.name}</span>
            <span className="rav-admin-row-actions">
              <button type="button" className="rav-text-link" data-testid={`status-edit-${i}`} onClick={() => setStatusForm({ ...s })}>Modifier</button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Campagnes ----------
function CampaignsManager({ campaigns, onChanged }) {
  const [form, setForm] = useState(null);
  const save = async () => {
    if (form.id) await adminPut(`/partner-campaigns/${form.id}`, form).catch(() => {});
    else await adminPost("/partner-campaigns", form).catch(() => {});
    toast("Campagne enregistrée.");
    setForm(null);
    onChanged();
  };
  return (
    <div data-testid="partners-campaigns">
      <button type="button" className="rav-secondary-button rav-session-btn" data-testid="campaign-create" onClick={() => setForm({ name: "", description: "", start_date: "", end_date: "", active: true })}>
        <Plus size={14} strokeWidth={1.6} /> Ajouter une campagne
      </button>
      {form && (
        <div className="rav-admin-form" data-testid="campaign-form">
          <input className="rav-input" data-testid="cf2-name" placeholder="Nom de la campagne (ex. Lancement RAV)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input className="rav-input" data-testid="cf2-description" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} style={{ marginTop: 8 }} />
          <div style={{ display: "flex", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
            <label className="rav-field"><span>Début</span><input className="rav-input" data-testid="cf2-start" type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value || null })} /></label>
            <label className="rav-field"><span>Fin</span><input className="rav-input" data-testid="cf2-end" type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value || null })} /></label>
            <label className="rav-field" style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
              <input type="checkbox" data-testid="cf2-active" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> <span>Active</span>
            </label>
          </div>
          <div className="rav-session-actions">
            <button type="button" className="rav-secondary-button rav-session-btn" data-testid="cf2-save" onClick={save}>Enregistrer</button>
            <button type="button" className="rav-text-link" data-testid="cf2-cancel" onClick={() => setForm(null)}>Annuler</button>
          </div>
        </div>
      )}
      <div className="rav-admin-list">
        {campaigns.map((c, i) => (
          <div key={c.id} className="rav-admin-row" data-testid={`campaign-row-${i}`}>
            <span className="rav-admin-row-label">{c.name}{!c.active ? " (inactive)" : ""}</span>
            <span className="rav-admin-row-sub">{c.description || ""} {c.start_date ? `· du ${fmtDate(c.start_date)}` : ""}{c.end_date ? ` au ${fmtDate(c.end_date)}` : ""}</span>
            <span className="rav-admin-row-actions">
              <button type="button" className="rav-text-link" data-testid={`campaign-edit-${i}`} onClick={() => setForm({ ...c, start_date: c.start_date || "", end_date: c.end_date || "" })}>Modifier</button>
              <button type="button" className="rav-text-link" data-testid={`campaign-delete-${i}`} onClick={() => adminDel(`/partner-campaigns/${c.id}`).then(onChanged)}><Trash2 size={13} strokeWidth={1.6} /></button>
            </span>
          </div>
        ))}
        {campaigns.length === 0 && <p className="rav-page-sub">Aucune campagne pour le moment (ex. Lancement RAV, Noël, Sommeil…).</p>}
      </div>
    </div>
  );
}
